import { ArrowRight } from "lucide-react";
import { LenderLogo } from "./LenderLogo";
import { applyUrl, type Lender } from "@/lib/lenders";
import { useEpi } from "@/lib/epi";

const nf = new Intl.NumberFormat("sv-SE");

/**
 * Staplad topplista med företagskort – exakt samma utseende som lånekalkylsidan.
 * Används både på /guider/lanekalkyl och på service-sidorna.
 */
export function LenderTable({
  lenders,
  title,
  note,
  epi2 = "comparison-table",
}: {
  lenders: Lender[];
  title?: string;
  note?: string;
  epi2?: string;
}) {
  const epi = useEpi() ?? "guider-lanekalkyl";
  if (!lenders.length) return null;
  return (
    <div className="not-prose my-6">
      {title && (
        <h2 className="mb-3 font-display text-2xl font-extrabold tracking-tight text-foreground">
          {title}
        </h2>
      )}
      <div className="overflow-hidden rounded-md border border-border bg-card">
        <div className="hidden grid-cols-[1.4fr_1fr_1fr_1.3fr_1fr_0.9fr_auto] gap-3 border-b border-border px-4 py-3 text-xs font-bold uppercase tracking-wide text-muted-foreground lg:grid">
          <span>Långivare</span>
          <span>Nominell ränta</span>
          <span>Effektiv ränta</span>
          <span>Lånebelopp</span>
          <span>Uppläggningsavgift</span>
          <span>AVI-avgift</span>
          <span />
        </div>
        {lenders.map((l) => (
          <div key={l.id} className="border-b border-border last:border-0">
            <div className="grid gap-3 px-4 py-4 lg:grid-cols-[1.4fr_1fr_1fr_1.3fr_1fr_0.9fr_auto] lg:items-center">
              <div className="flex items-center gap-3">
                <LenderLogo lender={l} size={110} bare className="!h-8 w-24" />
              </div>
              <Cell label="Nominell ränta" value={l.rate} />
              <Cell label="Effektiv ränta" value={l.effRate ?? l.rate} />
              <Cell
                label="Lånebelopp"
                value={`${nf.format(l.minAmount)} - ${nf.format(l.maxAmount)} kr`}
              />
              <Cell label="Uppläggningsavgift" value={l.setupFee} />
              <Cell label="AVI-avgift" value={l.invoiceFee} />
              <a
                href={applyUrl(l, epi, epi2)}
                target="_blank"
                rel="sponsored noopener"
                aria-label={`Gå vidare till ${l.name}`}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-cta px-5 py-2.5 text-sm font-bold !text-cta-foreground !no-underline transition hover:brightness-105"
              >
                Gå vidare <ArrowRight className="h-4 w-4" />
              </a>
            </div>
            <p className="bg-muted/40 px-4 py-3 text-[11.5px] leading-relaxed text-muted-foreground">
              {l.example}
            </p>
          </div>
        ))}
        <p className="px-4 py-3 text-[11.5px] text-muted-foreground">
          {note ??
            "Räntorna är exempelräntor och sätts individuellt av respektive långivare. Den effektiva räntan beräknas enligt Konsumentverkets riktlinjer."}
        </p>
      </div>
    </div>
  );
}

function Cell({ label, value }: { label: string; value: string }) {
  return (
    <div className="text-sm text-foreground">
      <span className="mr-2 text-xs font-semibold text-muted-foreground lg:hidden">{label}:</span>
      {value}
    </div>
  );
}
